<template>
  <div class="basic-layout">
    <AsyncIpBlocked v-if="ipBlock" />
    <template v-else>
      <BasicHeader />
      <main
        :style="mainStyle"
        class="basic-main"
      >
        <slot />
      </main>
      <Footer />
      <Activity />
    </template>
    <LoginDialog ref="loginDialog" />
  </div>
</template>

<script
  lang="ts"
  setup
>
import {
  computed,
  defineAsyncComponent,
  provide,
  ref,
  toRefs,
  useI18n,
  useSeoMeta,
  watch,
} from '#imports'
import LoginDialog from '@/layouts/common/LoginDialog.vue'
import { useLayout } from '@/store/layout'
import { useUserInfo } from '@/store/userInfo'
import $track from '@/track/webOfficialWebsite'
import { hydrateOnVisible } from 'vue'
import usePathNoLocale from '~/composables/usePathNoLocale'
import Activity from '~/layouts/basic/Activity.vue'
import BasicHeader from '~/layouts/basic/BasicHeader.vue'
import { OG_SITE_NAME } from '~/types/constants'
import {
  InjectionKey,
  RoutePath,
} from '~/types/enum'
import Footer from './basic/Footer.vue'

const { t } = useI18n()

useSeoMeta({
  ogSiteName: OG_SITE_NAME,
  description: () => t('page-description'),
  ogType: 'website',
  ogDescription: () => t('page-description'),
})

const pathNoLocale = usePathNoLocale()

const { isMobile } = toRefs(useLayout())

const mainStyle = computed(() => (
  (isMobile.value || pathNoLocale?.value !== RoutePath.HOME)
    ? { marginTop: 'var(--header-height)' }
    : undefined
))

const AsyncIpBlocked = defineAsyncComponent({
  loader: () => import('@/components/IpBlocked.vue'),
  hydrate: hydrateOnVisible(),
})

const loginDialog = ref<InstanceType<typeof LoginDialog>>()
const showLoginDialog: ShowLoginDialog = (callback) => loginDialog.value?.show(callback)

provide<ShowLoginDialog>(InjectionKey.SHOW_LOGIN_DIALOG, showLoginDialog)
const ipBlock = computed(() => !!useUserInfo().ipBlock)

const trackOfficialPageShow = (_path: RoutePath) => {
  switch (_path) {
    case RoutePath.HOME:
      return
    case RoutePath.MOBILE_APP:
      return $track.web_official_mobile_page_show()
    case RoutePath.DESKTOP_APP:
      return $track.web_official_desktop_page_show()
    case RoutePath.BROWSER_EXTENSION:
      return $track.web_official_extension_page_show()
    case RoutePath.HELP:
      return $track.web_official_help_page_show()
    case RoutePath.FAQ:
      return $track.web_official_faq_page_show()
    default:
      break
  }
}

watch(pathNoLocale, async val => {
  if (import.meta.server) return
  trackOfficialPageShow(val)
}, { immediate: true })

</script>

<style
  lang="less"
  scoped
>
@import '@/assets/styles/util.less';

.basic-layout {
  display: flex;
  flex-direction: column;
  min-height: var(--vh, 100vh);
}

.basic-main {
  min-height: calc(var(--vh, 100vh) - var(--header-height) - var(--footer-height));
  display: flex;
  flex-direction: column;
}
</style>
